import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import MuiTooltip from "@mui/material/Tooltip";
import { urlDisplayCandidates } from "@/utils/shortenUrl";

// A URL in a fact value, shown whole when it fits its column and otherwise
// as the most complete Slack-style shorter form that does (#12010). Fit is
// measured, not guessed from a character count: the first build shortened
// links that had room to spare. The `href` is always the full address, and
// so is what a copy of the link's text puts on the clipboard.

/** The nearest ancestor that lays out as a block: its content width is the
 *  room the link has. A URL that starts mid-line in a multiline value is
 *  measured against the same full width and may wrap once chosen; that is
 *  accepted, since it still cannot escape the column. */
const blockAncestor = (el: HTMLElement): HTMLElement | null => {
  let node = el.parentElement;
  while (node && getComputedStyle(node).display.startsWith("inline")) {
    node = node.parentElement;
  }
  return node;
};

const contentWidth = (el: HTMLElement): number => {
  const style = getComputedStyle(el);
  return (
    el.clientWidth -
    parseFloat(style.paddingLeft) -
    parseFloat(style.paddingRight)
  );
};

/** The canvas font shorthand for what `el` renders its text in, or null
 *  when `el` has no computed style (it is no longer in the document). */
const fontOf = (el: HTMLElement): string | null => {
  const style = getComputedStyle(el);
  if (style.fontSize === "") return null;
  return `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
};

const UrlLink: React.FC<{ href: string }> = ({ href }) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const candidates = useMemo(() => urlDisplayCandidates(href), [href]);
  const [index, setIndex] = useState(0);

  useLayoutEffect(() => {
    // Read the anchor at every fit rather than once: a re-render replaces
    // the node, and a measurement through the old one reads an empty font.
    const fit = () => {
      const el = ref.current;
      const block = el ? blockAncestor(el) : null;
      const font = el ? fontOf(el) : null;
      if (!el || !block || font === null) return;
      canvasRef.current ??= document.createElement("canvas");
      const context = canvasRef.current.getContext("2d");
      if (!context) return;
      context.font = font;
      const room = contentWidth(block);
      const first = candidates.findIndex(
        (c) => context.measureText(c.text).width <= room
      );
      setIndex(first === -1 ? candidates.length - 1 : first);
    };
    fit();
    const block = ref.current ? blockAncestor(ref.current) : null;
    if (!block) return;
    const observer = new ResizeObserver(fit);
    observer.observe(block);
    // A fit made before the web font arrived measured the fallback face,
    // which can be several percent narrower; measure again once it is in.
    let cancelled = false;
    void document.fonts.ready.then(() => {
      if (!cancelled) fit();
    });
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [candidates]);

  // Between an `href` change and the layout effect that follows it, the
  // stored index may point past the new, shorter list.
  const shown = candidates[Math.min(index, candidates.length - 1)];

  // A copy of the visible text is a copy of the address: the shortened form
  // is for reading, and pasting it anywhere would give a broken link. A
  // selection that runs past the anchor is left alone, since replacing it
  // would drop the rest of what was selected.
  const onCopy = (event: React.ClipboardEvent<HTMLAnchorElement>) => {
    const el = ref.current;
    const selection = window.getSelection();
    if (!el || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!el.contains(range.startContainer) || !el.contains(range.endContainer))
      return;
    event.clipboardData.setData("text/plain", href);
    event.preventDefault();
  };

  // Always inside the tooltip, with an empty title when nothing was left
  // out (MUI then shows nothing): switching between a bare anchor and a
  // wrapped one remounts the anchor under the measuring code. The anchor
  // sets its own aria-label so an empty title leaves no empty attribute
  // behind. A value never hover-repeats its own text (#11426).
  //
  // The wrap-anywhere is the guard for what no shorter form can fit — a long
  // single segment in a narrow column — so the link never runs out of its
  // card.
  return (
    <MuiTooltip title={shown.elided ? href : ""}>
      <a
        ref={ref}
        href={href}
        aria-label={shown.elided ? href : undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline [overflow-wrap:anywhere]"
        onClick={(e) => e.stopPropagation()}
        onCopy={onCopy}
      >
        {shown.text}
      </a>
    </MuiTooltip>
  );
};

export default UrlLink;
